import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SCHUBFACH (Auftrag 17.09.2026, Abschnitt 16; Barrierefreiheit Abschnitt 28)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   „Bei umfangreichen technischen Eigenschaften bevorzugt Side Drawer statt
 *    winziger Modals."
 *
 * Genau dafür ist dieses Muster da: dreißig Kennwerte eines Lüftungsgeräts,
 * die Filterliste einer Bauteiltabelle, die Historie eines Auftrags. Der
 * Unterschied zum Dialog ist nicht die Optik, sondern die Menge.
 *
 * WAS ES VON SICH AUS TUT (Abschnitt 28/29)
 * -----------------------------------------
 * - ESC schließt.
 * - Der Tastaturfokus wandert beim Öffnen hinein und bleibt drin
 *   (Fokusfalle); beim Schließen kehrt er auf das auslösende Element zurück.
 * - `role="dialog"`, `aria-modal`, `aria-labelledby` auf die Überschrift.
 * - Klick auf die abgedunkelte Fläche schließt - wie bei den vorhandenen
 *   Dialogen der Anwendung.
 *
 * Es sperrt das Rollen der Seite dahinter NICHT: In dieser Anwendung steht
 * links regelmäßig eine Tabelle, in der man weiterlesen will, während rechts
 * die Kennwerte stehen.
 */

/** Alles, was den Tastaturfokus annehmen kann. */
const FOKUSSIERBAR = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])', 'details',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface DrawerProps {
  offen: boolean;
  /** Überschrift; zugleich der Vorlesename des Schubfachs. */
  titel: string;
  beschreibung?: ReactNode;
  children: ReactNode;
  beiSchliessen: () => void;
  /** Knöpfe am unteren Rand, z. B. [Übernehmen] [Abbrechen]. */
  fuss?: ReactNode;
  /** Von welcher Seite. Vorgabe rechts. */
  seite?: 'rechts' | 'links';
  /** Breite, z. B. `'480px'`. Vorgabe 440px. */
  breite?: string;
  className?: string;
}

export function Drawer({
  offen, titel, beschreibung, children, beiSchliessen, fuss,
  seite = 'rechts', breite, className,
}: DrawerProps) {
  const flaeche = useRef<HTMLDivElement | null>(null);
  const vorher = useRef<HTMLElement | null>(null);
  const titelId = useId();

  useEffect(() => {
    if (!offen) return undefined;

    vorher.current = document.activeElement as HTMLElement | null;
    // Ein Tick später: die Fläche entsteht erst mit dem Rendern.
    const id = window.setTimeout(() => {
      const erstes = flaeche.current?.querySelector<HTMLElement>(FOKUSSIERBAR);
      (erstes ?? flaeche.current)?.focus();
    }, 0);

    return () => {
      window.clearTimeout(id);
      vorher.current?.focus?.();
    };
  }, [offen]);

  if (!offen) return null;

  function beiTaste(ereignis: React.KeyboardEvent) {
    if (ereignis.key === 'Escape') {
      ereignis.preventDefault();
      beiSchliessen();
      return;
    }
    if (ereignis.key !== 'Tab') return;

    // Fokusfalle: Am Ende der Liste geht es wieder an den Anfang. Ohne sie
    // wandert der Fokus hinter das Schubfach in die verdeckte Seite - man
    // tippt dann in etwas, das man nicht sieht.
    const ziele = [...(flaeche.current?.querySelectorAll<HTMLElement>(FOKUSSIERBAR) ?? [])];
    if (ziele.length === 0) return;
    const erstes = ziele[0];
    const letztes = ziele[ziele.length - 1];
    const aktiv = document.activeElement;

    if (!ereignis.shiftKey && aktiv === letztes) {
      ereignis.preventDefault();
      erstes.focus();
    } else if (ereignis.shiftKey && aktiv === erstes) {
      ereignis.preventDefault();
      letztes.focus();
    }
  }

  return (
    <div
      className="m-schubfach-hintergrund"
      onMouseDown={(e) => { if (e.target === e.currentTarget) beiSchliessen(); }}
    >
      <div
        className={`m-schubfach m-schubfach-${seite}${className ? ` ${className}` : ''}`}
        style={breite ? { width: breite } : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titelId}
        ref={flaeche}
        tabIndex={-1}
        onKeyDown={beiTaste}
      >
        <header className="m-schubfach-kopf">
          <div>
            <h2 className="m-schubfach-titel" id={titelId}>{titel}</h2>
            {beschreibung && (
              <p className="m-schubfach-beschreibung">{beschreibung}</p>
            )}
          </div>
          <button
            type="button"
            className="m-knopf m-knopf-nur-zeichen m-schubfach-schliessen"
            onClick={beiSchliessen}
            aria-label="Schubfach schließen"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className="m-schubfach-inhalt">{children}</div>

        {fuss && <footer className="m-schubfach-fuss">{fuss}</footer>}
      </div>
    </div>
  );
}
